import React, { useMemo, useState } from 'react';
import { decoyKeyMatches } from '../../lib/decoy';

/**
 * A converter that converts.
 *
 * Third of the disguises, and the same requirement as the other two: it has to
 * work, because a curious person who finds a dead app now knows something is
 * wrong AND has become interested.
 *
 * TWO KINDS OF DATA, TWO KINDS OF INTERACTION, and the difference is the
 * Founder's -- a trained electrician -- not a design preference. Length and
 * mass are continuous: two fields, live, either direction. Wire gauge is NOT.
 * AWG sizes are discrete steps, and the trade reads them off a table rather
 * than typing into a field, so that is a table.
 *
 * WHAT THE TABLE DELIBERATELY DOES NOT SHOW:
 *
 *   - A "nearest standard cross-section" column. With installation cable you
 *     round up, but you never write the rounded number down: the conversion
 *     gives the true cross-section, and choosing the cable you can actually buy
 *     is a separate decision made afterwards. Printing the rounded figure as if
 *     it were the answer confuses a conversion with a design choice.
 *   - A maximum current. The usual figure in an AWG chart is 700 circular mils
 *     per ampere, which is free-air power transmission -- it gives AWG 14 as
 *     5.9 A, where the same conductor in a Danish installation is rated several
 *     times that. A number that is right under a convention the reader does not
 *     work in is more misleading than no column at all.
 *   - kcmil, inches, square inches, milliohms per foot. Imperial, and of no
 *     use in a metric installation.
 *
 * Every figure is CALCULATED, not transcribed. AWG is an exact definition, so
 * there is nothing to mistype: diameter = 0.127 x 92^((36-n)/39) mm, area from
 * that, resistance from copper's resistivity at 20 C. Checked against a
 * published chart and agreeing to 0.03% on resistance and 0.5% on the rest,
 * which is that chart's own rounding.
 *
 * The table stops at AWG 24. Below it, two decimals stop distinguishing sizes
 * -- 34 and 35 both read 0.02 -- and nothing that thin is installation cable.
 *
 * Deliberately no Arcanum in here: no gold, no serif, no theme tokens.
 */

const RHO_COPPER = 1.724e-8;   // ohm-metre, at 20 C

const awgRows = () => {
  const out = [];
  // 4/0 .. 1/0 are n = -3 .. 0, then 1 .. 24.
  const sizes = [[-3, '4/0'], [-2, '3/0'], [-1, '2/0'], [0, '1/0']];
  for (let n = 1; n <= 24; n += 1) sizes.push([n, String(n)]);
  sizes.forEach(([n, label]) => {
    const mm = 0.127 * 92 ** ((36 - n) / 39);
    const area = (Math.PI / 4) * mm * mm;
    const ohm = (RHO_COPPER / (area * 1e-6)) * 1000;   // milliohm per metre
    out.push({ label, mm, area, ohm });
  });
  return out;
};

const da = (n, decimals) => n.toFixed(decimals).replace('.', ',');
/** Three significant figures, so 4/0 and 3/0 do not both read 0,2. */
const sig3 = (n) => {
  const s = n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1) : n >= 1 ? n.toFixed(2) : n.toFixed(3);
  return s.replace('.', ',');
};

const num = (s) => {
  const v = parseFloat(String(s).replace(',', '.'));
  return Number.isFinite(v) ? v : null;
};
const show = (v) => (v === null ? '' : String(Math.round(v * 10000) / 10000).replace('.', ','));

const BG = '#1B242C';
const SURFACE = '#24303A';
const RAISED = '#2C3B47';
const INK = '#E8EDF1';
const MUTED = '#93A1AC';
const FAINT = '#6B7A86';
const LINE = '#33434F';

/**
 * Two fields that are the same quantity in two units.
 *
 * No swap button. Both fields are editable and each follows the other, so
 * there is nothing for a swap to do -- and a control that appears to do
 * nothing is exactly what makes an app feel fake. The mockup had one; it was
 * decorative there too.
 */
const Pair = ({ initial, unitA, unitB, factor, onChangeA }) => {
  const [a, setA] = useState(initial);
  const [b, setB] = useState(() => show(num(initial) * factor));

  const editA = (v) => {
    setA(v);
    const n = num(v);
    setB(n === null ? '' : show(n * factor));
    if (onChangeA) onChangeA(v);
  };

  const editB = (v) => {
    setB(v);
    const n = num(v);
    setA(n === null ? '' : show(n / factor));
  };

  const field = (value, unit, onEdit, side) => (
    <label
      style={{
        background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12,
        padding: '14px 16px', display: 'flex', alignItems: 'baseline', gap: 12,
      }}
    >
      <input
        type="text"
        inputMode="decimal"
        value={value}
        aria-label={unit}
        data-testid={`decoy-conv-${side}`}
        onChange={(e) => onEdit(e.target.value)}
        style={{
          flex: 1, minWidth: 0, appearance: 'none', background: 'transparent',
          border: 0, color: INK, font: 'inherit', fontSize: 30, fontWeight: 500,
          fontVariantNumeric: 'tabular-nums', padding: 0, outline: 'none',
        }}
      />
      <span style={{ fontSize: 14, color: MUTED, fontWeight: 500, whiteSpace: 'nowrap' }}>{unit}</span>
    </label>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {field(a, unitA, editA, 'a')}
      <div style={{ height: 1, background: LINE, margin: '2px 12px' }} />
      {field(b, unitB, editB, 'b')}
    </div>
  );
};

const Converter = ({ onOpen }) => {
  const [tab, setTab] = useState('len');
  const rows = useMemo(awgRows, []);

  // The door. Live rather than behind a button, because a converter has no
  // button to press -- the value is simply what is in the field. A number that
  // is not the key is converted, which is what a converter does, so there is
  // nothing to notice and nothing to probe.
  const watchCm = (typed) => {
    if (decoyKeyMatches(String(typed).trim())) onOpen();
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      data-testid={`decoy-conv-tab-${id}`}
      style={{
        appearance: 'none', border: 0, background: tab === id ? RAISED : 'transparent',
        color: tab === id ? INK : MUTED, font: 'inherit', fontSize: 14, fontWeight: 500,
        padding: '9px 0', borderRadius: 7, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200, background: BG, color: INK,
        display: 'flex', flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif', fontSize: 16,
      }}
      data-testid="decoy-converter"
    >
      <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>Omregner</h1>
        <div
          role="tablist"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: SURFACE,
            borderRadius: 10, padding: 4, gap: 4,
          }}
        >
          {tabBtn('len', 'Længde')}
          {tabBtn('mass', 'Vægt')}
          {tabBtn('awg', 'Kabel')}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 20px 28px' }}>
        {tab === 'len' && (
          <Pair initial="100" unitA="cm" unitB="tommer" factor={1 / 2.54} onChangeA={watchCm} />
        )}

        {tab === 'mass' && (
          <Pair initial="1000" unitA="gram" unitB="ounce" factor={1 / 28.349523125} />
        )}

        {tab === 'awg' && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                <thead>
                  <tr>
                    {['AWG', 'Ø mm', 'mm²', 'mΩ/m'].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase',
                          color: FAINT, fontWeight: 600, borderBottom: `1px solid ${LINE}`,
                          padding: '0 6px 8px', textAlign: i === 0 ? 'left' : 'right',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="decoy-conv-awg">
                  {rows.map((r) => (
                    <tr key={r.label} style={{ borderBottom: '1px solid rgba(51,67,79,0.5)' }}>
                      <td style={{ padding: '9px 6px', textAlign: 'left', fontWeight: 600, fontSize: 14.5 }}>{r.label}</td>
                      <td style={{ padding: '9px 6px', textAlign: 'right', fontSize: 14.5, color: MUTED }}>{da(r.mm, 2)}</td>
                      <td style={{ padding: '9px 6px', textAlign: 'right', fontSize: 14.5, fontWeight: 600 }}>{da(r.area, 2)}</td>
                      <td style={{ padding: '9px 6px', textAlign: 'right', fontSize: 14.5, color: MUTED }}>{sig3(r.ohm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 12, color: FAINT, margin: '12px 0 0', lineHeight: 1.45 }}>
              Tværsnittet er det beregnede. Skal der vælges installationskabel,
              rundes der op til nærmeste faste tværsnit — men det er et valg,
              ikke en omregning.
            </p>
            <p style={{ fontSize: 12, color: FAINT, margin: '8px 0 0', lineHeight: 1.45 }}>
              Modstand for kobber ved 20 °C.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Converter;
